/**
 * Spaces — 手机配对过的所有项目列表（应用主页）。
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiError, listSpaces, type SpaceOut } from '../lib/api'
import { clearToken } from '../lib/auth'
import { useLang } from '../contexts/LanguageContext'
import InstallPrompt from '../components/InstallPrompt'
import { Spinner, CenterNote, LangToggle } from '../components/ui'
import { IconRefresh, IconScan, IconChevronRight } from '../components/icons'

type Translate = (id: string, values?: Record<string, string | number>) => string

function relativeTime(iso: string, t: Translate): string {
  const diff = Date.now() - new Date(iso).getTime()
  if (diff < 60_000) return t('time.justNow')
  if (diff < 3_600_000) return t('time.minutesAgo', { n: Math.floor(diff / 60_000) })
  if (diff < 86_400_000) return t('time.hoursAgo', { n: Math.floor(diff / 3_600_000) })
  return t('time.daysAgo', { n: Math.floor(diff / 86_400_000) })
}

export default function Spaces() {
  const navigate = useNavigate()
  const { t } = useLang()
  const [spaces, setSpaces] = useState<SpaceOut[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const refresh = useCallback(async () => {
    setRefreshing(true)
    setError(null)
    try {
      const res = await listSpaces()
      setSpaces(res.spaces)
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        clearToken()
        navigate('/pair', { replace: true })
        return
      }
      setError((e as Error)?.message?.slice(0, 200) || t('common.loadFailed'))
    } finally {
      setRefreshing(false)
    }
  }, [navigate, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-line-subtle bg-canvas/92 px-4 pb-3 pt-safe backdrop-blur">
        <h1 className="font-heading text-2xl tracking-tight text-primary">{t('spaces.title')}</h1>
        <div className="flex items-center gap-1">
          <LangToggle />
          <button
            onClick={() => void refresh()}
            disabled={refreshing}
            aria-label={t('common.refresh')}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-muted active:bg-hover disabled:opacity-50"
          >
            <IconRefresh className={`h-5 w-5 ${refreshing ? '[animation:spin_0.8s_linear_infinite]' : ''}`} />
          </button>
          <button
            onClick={() => navigate('/pair')}
            aria-label={t('spaces.pairNew')}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-muted active:bg-hover"
          >
            <IconScan className="h-5 w-5" />
          </button>
        </div>
      </header>

      <div className="flex flex-1 flex-col px-4 pt-4">
        <InstallPrompt />

        {error && (
          <div className="reveal mt-3 rounded-lg border border-error/30 bg-error/10 px-3.5 py-3 text-sm text-error">
            {error}
          </div>
        )}

        {spaces === null && !error && (
          <CenterNote>
            <Spinner className="h-6 w-6 text-accent" />
            <p className="text-sm text-muted">{t('common.loading')}</p>
          </CenterNote>
        )}

        {spaces && spaces.length === 0 && !error && (
          <CenterNote>
            <p className="font-heading text-lg text-primary">{t('spaces.emptyTitle')}</p>
            <p className="max-w-[16rem] text-sm leading-relaxed text-dimmed">{t('spaces.emptyDesc')}</p>
            <button
              onClick={() => navigate('/pair')}
              className="mt-1 flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-medium text-white min-h-tap"
            >
              <IconScan className="h-4 w-4" />
              {t('pair.scanToPair')}
            </button>
          </CenterNote>
        )}

        {spaces && spaces.length > 0 && (
          <ul className="mt-3 space-y-2">
            {spaces.map((sp, i) => (
              <li key={sp.id} className="reveal" style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}>
                <button
                  onClick={() => navigate(`/spaces/${sp.id}`, { state: { title: sp.title } })}
                  className="flex w-full items-center gap-3 rounded-lg border border-line-subtle bg-panel px-4 py-3.5 text-left transition-colors active:bg-elevated min-h-tap"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-medium text-primary">
                      {sp.title || t('spaces.untitled')}
                    </div>
                    <div className="mt-0.5 text-xs text-dimmed">{relativeTime(sp.last_used_at, t)}</div>
                  </div>
                  <IconChevronRight className="h-4 w-4 shrink-0 text-faint" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
